import { useEffect, useState } from 'react'
import { storageManager } from '../lib/storage'
import { validateConfig } from '../lib/config'

interface StorageInitializerProps {
  children: React.ReactNode
  onReady?: () => void
  onError?: (error: Error) => void
}

export function StorageInitializer({ children, onReady, onError }: StorageInitializerProps) {
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    async function initializeStorage() {
      try {
        // Validate environment configuration
        if (!validateConfig()) {
          throw new Error('Invalid configuration. Check environment variables.')
        }

        // Initialize storage buckets (if admin client is available)
        await storageManager.initializeBuckets()
        
        setIsReady(true)
        onReady?.()
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Storage initialization failed')
        setError(error)
        onError?.(error)
        console.error('Storage initialization failed:', error)
      }
    }

    initializeStorage()
  }, [onReady, onError])

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <h3 className="text-red-800 font-medium">Storage Connection Error</h3>
        <p className="text-red-600 text-sm mt-1">{error.message}</p>
        <p className="text-red-500 text-xs mt-2">
          Please check your Supabase configuration and environment variables.
        </p>
      </div>
    )
  }

  if (!isReady) {
    return (
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-blue-800 text-sm">Initializing storage...</span>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
